import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, ShoppingBag, Users, IndianRupee } from 'lucide-react';
import RevenueCard from '@/components/admin/RevenueCard';
import RevenueChart from '@/components/admin/RevenueChart';
import InventoryOverview from '@/components/admin/InventoryOverview';
import TopSellingProducts from '@/components/admin/TopSellingProducts';
import LowStockAlert from '@/components/admin/LowStockAlert';
import LiveOrders from '@/components/admin/LiveOrders';

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const ready = !!user && isAdmin;

  const { data: productCount = 0 } = useQuery({
    queryKey: ['admin', 'product-count'],
    enabled: ready,
    queryFn: async () => {
      const { count } = await supabase.from('products').select('*', { count: 'exact', head: true });
      return count || 0;
    },
  });

  const { data: orderCount = 0 } = useQuery({
    queryKey: ['admin', 'order-count'],
    enabled: ready,
    queryFn: async () => {
      const { count } = await supabase.from('orders').select('*', { count: 'exact', head: true });
      return count || 0;
    },
  });

  const { data: userCount = 0 } = useQuery({
    queryKey: ['admin', 'user-count'],
    enabled: ready,
    queryFn: async () => {
      const { count } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
      return count || 0;
    },
  });

  const { data: revenue = 0 } = useQuery({
    queryKey: ['admin', 'revenue'],
    enabled: ready,
    queryFn: async () => {
      const { data } = await supabase.from('orders').select('total').eq('payment_status', 'paid');
      return data?.reduce((sum, o) => sum + Number(o.total), 0) || 0;
    },
  });

  const stats = [
    { label: 'Products', value: productCount, icon: Package, color: 'bg-sky/10 text-sky' },
    { label: 'Orders', value: orderCount, icon: ShoppingBag, color: 'bg-warning/10 text-warning' },
    { label: 'Users', value: userCount, icon: Users, color: 'bg-success/10 text-success' },
    { label: 'Revenue', value: `₹${revenue.toLocaleString('en-IN')}`, icon: IndianRupee, color: 'bg-primary/10 text-primary' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-bold">Dashboard</h1>
      
      {/* Revenue Tracking Section */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold text-muted-foreground">Revenue Analytics</h2>
        <RevenueCard />
      </div>

      {/* Inventory Management Section */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold text-muted-foreground">Inventory Management</h2>
        <InventoryOverview />
      </div>

      {/* Sales & Alerts Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        <TopSellingProducts />
        <LowStockAlert />
      </div>

      {/* Charts & Live Data Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        <RevenueChart />
        <LiveOrders />
      </div>

      {/* Original Stats */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold text-muted-foreground">General Statistics</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(stat => (
            <Card key={stat.label}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.label}</CardTitle>
                <div className={`h-8 w-8 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="font-heading text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
